import type { QueryClient } from "@tanstack/react-query";

/**
 * fast_pages flag: the journal list is cached in React Query. Any write that
 * creates, edits, posts, cancels, deletes or restores a journal entry (manual
 * or automatic) must call notifyJournalListChanged() so the cached list is
 * invalidated immediately — the user must never see a list missing an entry
 * they just saved. The event decouples the shared posting gates (which have
 * no QueryClient) from the list page.
 */
export const JOURNAL_LIST_INVALIDATE_EVENT = "journal-list:invalidate";

/** Query-key roots whose data derives from journal entries. */
export const JOURNAL_DERIVED_KEYS = ["journal-list", "journal-entry", "dashboard", "ledger-report", "trial-balance-report"] as const;

/** Register at app lifetime, not list-route lifetime: writes happen off-list. */
export function subscribeJournalListInvalidation(queryClient: QueryClient) {
  const onInvalidate = () => {
    // Do not render pre-write data when returning to an unmounted cached page.
    // Removing inactive queries also cancels any obsolete page prefetch.
    // Same rule for the other fast_pages screens built on journal data.
    for (const key of JOURNAL_DERIVED_KEYS) {
      queryClient.removeQueries({ queryKey: [key], type: "inactive" });
      void queryClient.invalidateQueries({ queryKey: [key], type: "active" });
    }
  };
  window.addEventListener(JOURNAL_LIST_INVALIDATE_EVENT, onInvalidate);
  return () => window.removeEventListener(JOURNAL_LIST_INVALIDATE_EVENT, onInvalidate);
}

export function notifyJournalListChanged() {
  try {
    window.dispatchEvent(new Event(JOURNAL_LIST_INVALIDATE_EVENT));
  } catch {
    /* non-browser context (tests) — ignore */
  }
}
